import { Stack, Text, Group, Checkbox } from '@mantine/core';
import BigNumber from 'bignumber.js';
import cn from 'clsx';
import { formatDistance } from 'date-fns';
import { BiLinkExternal } from 'react-icons/bi';
import { useWithdrawalWaitTime } from 'src/api/smart-contracts';
import { ApiWithdrawalRequest } from 'src/types';
import { formatDecimal } from 'src/utils';
import { NETWORK_VARS } from 'src/constants';

export function ClaimRequest({
  request,
  hash,
  isChecked,
  isRedeeming,
  isRedeemed,
  onCheck,
}: {
  request: ApiWithdrawalRequest;
  hash?: string;
  isChecked: boolean;
  isRedeeming: boolean;
  isRedeemed: boolean;
  onCheck: () => void;
}) {
  const { withdrawalWaitTime } = useWithdrawalWaitTime();

  const getLabels = () => {
    if (isRedeemed || request.claimed) {
      return ['Successfully claimed'];
    }

    if (isRedeeming) {
      return ['Claiming...'];
    }

    if (request.is_claimable) {
      return ['Ready to claim'];
    }

    const now = Date.now();
    const timePassed = BigNumber(now).minus(
      BigNumber(request.requested_at || 0).times(1000)
    );
    const _timeLeft = withdrawalWaitTime.minus(timePassed);
    const timeLeft = _timeLeft.isNegative() ? 0 : _timeLeft.toNumber();
    const timeLeftHR = formatDistance(
      now,
      BigNumber(now).plus(timeLeft).toNumber()
    );

    return ['Pending', timeLeftHR];
  };

  const [label, pendingTimeLabel] = getLabels();

  const isDisabled = (!request.is_claimable && !request.claimed) || isRedeemed;

  const txHash = hash || request.withdrawal_tx_hash;

  const renderLabel = (className?: string) => {
    return (
      <Group gap={10} className={cn('max-lg:flex-row-reverse', className)}>
        {pendingTimeLabel && (
          <Text c="var(--mantine-color-main-2)">
            Available in {pendingTimeLabel === 'less than a minute' ? '' : '~'}
            {pendingTimeLabel}
          </Text>
        )}
        <Text
          bg={
            isRedeeming || isRedeemed || request.claimed || request.is_claimable
              ? 'aprioriPurple.0'
              : 'rgba(255,255,255,0.14)'
          }
          c="var(--mantine-color-main-0)"
          fw={700}
          className="h-4 px-2 lg:h-7 lg:px-3 flex items-center gap-1 rounded-[100px]"
        >
          {label}
          {txHash ? (
            <a
              href={`${NETWORK_VARS.explorer}/tx/${txHash}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <BiLinkExternal size={20} color="var(--mantine-color-main-0)" />
            </a>
          ) : null}
        </Text>
      </Group>
    );
  };

  return (
    <Group
      key={request.id}
      justify="space-between"
      h={72}
      className={cn(
        'rounded-[16px] px-4 border-[1px] border-solid border-primarypurple',
        isDisabled && 'border-white/[0.14]'
      )}
    >
      <Checkbox
        label={
          <Stack gap={4}>
            <span>
              {formatDecimal(BigNumber(request.assets).div(1e18), {
                roundingMode: BigNumber.ROUND_FLOOR,
              })}{' '}
              MON
            </span>
            {renderLabel('lg:hidden')}
          </Stack>
        }
        checked={isChecked}
        size="md"
        c={isDisabled ? 'var(--mantine-color-main-2)' : undefined}
        className={cn(isDisabled && 'pointer-events-none')}
        classNames={{
          inner: cn(isDisabled && 'hidden'),
          body: 'items-center',
          label: cn(isDisabled ? 'pl-0' : 'pl-2 lg:pl-4'),
        }}
        onChange={() => onCheck()}
      />

      {renderLabel('hidden lg:flex')}
    </Group>
  );
}
